import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import api from '../../utils/api';
import { MessageSquare, Clock, ArrowLeft, Hash, Trash2, Edit, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';

const MyPosts = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const authRes = await api.get('/auth/profile');
                setCurrentUser(authRes.data);
                const postsRes = await api.get('/api/forum/my-posts');
                setPosts(postsRes.data || []);
            } catch (error) {
                console.error('Failed to load your posts:', error);
                toast.error('Failed to load your discussions.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleDeletePost = async (e, postId) => {
        e.preventDefault();
        e.stopPropagation();
        if (!window.confirm("Are you sure you want to delete this post?")) return;
        try {
            await api.delete(`/api/admin/forum/posts/${postId}`);
            toast.success("Post deleted.");
            setPosts(posts.filter(p => p.id !== postId));
        } catch (error) {
            toast.error(error?.error || "Failed to delete post.");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex flex-col font-sans">
                <NavigationHeader />
                <div className="flex-1 flex items-center justify-center">
                    <Icon name="Loader2" className="animate-spin text-emerald-600" size={32} />
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <NavigationHeader />

            <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 pt-24 pb-20">
                {/* Breadcrumbs */}
                <div className="flex items-center gap-2 text-[10px] font-blacktracking-[0.2em] text-slate-400 mb-8">
                    <Link to="/community" className="hover:text-emerald-600 transition-colors">Community</Link>
                    <Icon name="ChevronRight" size={12} strokeWidth={3} />
                    <span className="text-slate-900">My Discussions</span>
                </div>

                <div className="flex flex-col sm:flex-row items-baseline justify-between gap-4 mb-12">
                    <div>
                        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Your Contributions</h1>
                        <p className="text-xs font-bold text-slate-400 mt-2tracking-widest">Manage your shared experiences and insights</p>
                    </div>
                    <Link to="/community/create" className="px-6 py-3 bg-[#059669] text-white text-[10px] font-blacktracking-[0.2em] rounded-xl hover:bg-[#047857] transition-all shadow-lg shadow-emerald-500/20">
                        New Post
                    </Link>
                </div>

                {posts.length === 0 ? (
                    <div className="text-center py-20 bg-[#F8FAFC] rounded-3xl border border-dashed border-slate-200">
                        <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                            <Icon name="MessageSquare" size={24} className="text-slate-300" />
                        </div>
                        <p className="text-sm font-normal text-slate-500">No discussions yet</p>
                        <p className="text-[11px] text-slate-500 mt-2 mb-8 max-w-xs mx-auto font-medium">You haven't shared any experiences or asked any questions yet.</p>
                        <Link to="/community/create" className="text-[10px] font-blacktracking-widest text-[#059669] hover:text-[#047857]">Create your first post</Link>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {posts.map(post => (
                            <div key={post.id} className="group bg-white rounded-3xl border border-slate-100 p-6 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/5 transition-all duration-300 relative overflow-hidden">
                                <Link to={`/community/post/${post.id}`} className="absolute inset-0 z-0" />
                                
                                <div className="relative z-10 flex flex-col gap-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className="px-2.5 py-1 bg-[#F0FDF4] text-[#166534] text-[9px] font-blacktracking-widest rounded-md border border-[#DCFCE7]">
                                                {post.category || 'Interview'}
                                            </span>
                                            <span className="text-[10px] font-bold text-slate-300tracking-widest">{new Date(post.created_at).toLocaleDateString()}</span>
                                        </div>
                                        <div className="flex items-center gap-2 relative z-20">
                                            <Link to={`/community/post/${post.id}/edit`} className="p-2 text-slate-400 hover:text-emerald-600 bg-[#F8FAFC] hover:bg-emerald-50 rounded-lg transition-all">
                                                <Icon name="Pencil" size={14} />
                                            </Link>
                                            <button onClick={(e) => handleDeletePost(e, post.id)} className="p-2 text-slate-400 hover:text-red-600 bg-[#F8FAFC] hover:bg-red-50 rounded-lg transition-all">
                                                <Icon name="Trash2" size={14} />
                                            </button>
                                        </div>
                                    </div>

                                    <h2 className="text-lg font-bold text-slate-900 group-hover:text-emerald-700 transition-colors tracking-tight leading-snug">
                                        {post.title}
                                    </h2>

                                    <div className="flex items-center gap-6 pt-2">
                                        <div className="flex items-center gap-2 text-[10px] font-blacktracking-widest text-slate-400">
                                            <Icon name="MessageSquare" size={14} />
                                            <span>{post.comment_count} Comments</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-[10px] font-blacktracking-widest text-slate-400">
                                            <Icon name="ThumbsUp" size={14} />
                                            <span>{post.upvotes - post.downvotes} Upvotes</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-[10px] font-blacktracking-widest text-slate-400">
                                            <Icon name="Eye" size={14} />
                                            <span>{post.views_count || 0} Views</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
};

export default MyPosts;
